import React, { ChangeEvent, useEffect, useState } from "react";
import Card from "components/card";
import { MdDelete as DeleteIcon, MdEdit as EditIcon } from "react-icons/md";
import { BsFillNodePlusFill, BsArrowDown, BsArrowUp, BsSearch } from "react-icons/bs";
import InputField from "components/fields/InputField";
import GeneralModal from "usefulComponents/Modal/Modal";
import ThemedSelect from "components/fields/ThemedSelect";
import DataTable from "components/Datatable/Datatable";
import { GridColDef } from "@mui/x-data-grid";
import { deleteDataById, fetchData, insertData, updatetDataById } from "api";
import { symptoms, systems } from "api/collection_names";
import { Button } from "@mui/material";
import { SelectModel } from "utils/general-utils";
import { useNavigate } from "react-router-dom";
import { SnackbarState } from "models/user-models";
import AppSnackbar from "components/snakbar";
import SearchFilterModal, { SearchOption } from "components/searchFilterModal/SearchFilterModal";
import {
  getBasePriority,
  getSystemPriority,
  createInitialPriority,
  ensureSystemPriorities,
} from "utils/priority-utils";

export interface ScrollDown {
  description: string;
  priority: number;
}

export interface ComplaintsType {
  description: string;
  system_id: string[];
  scroll_downs: ScrollDown[];
  priority?: number | { base: number; [system_id: string]: number };
  _id?: string;
}

export interface UpdateComplaintsType {
  description: string;
  system_id: string[];
  scroll_downs: ScrollDown[];
  _id?: string;
}

const Complaints = () => {
  const [open, setOpen] = React.useState(false);
  const [isEdit, setIsEdit] = React.useState(false);
  const [editId, setEditId] = React.useState("0");
  const [allComplaints, setAllComplaints] = useState<ComplaintsType[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [isOs, setIsOs] = useState<boolean>(false);
  const [osData, setOsData] = useState([] as ComplaintsType[]);

  // Search filter states
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchType, setSearchType] = useState<"description" | "system" | null>(null);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [searchLabel, setSearchLabel] = useState<string | null>(null);
  const isSearchActive = Boolean(searchType && searchValue);

  const [dataObj, setDataObj] = useState<ComplaintsType>({
    description: "",
    system_id: [],
    scroll_downs: [],
    priority: 0,
  });

  const [dataObjUpdate, setDataObjUpdate] = useState<UpdateComplaintsType>({
    description: "",
    system_id: [],
    scroll_downs: [],
  });

  const [options, setOptions] = useState<SelectModel[]>([{ value: "null", label: "None" }]);
  const [loading, setLoading] = useState<boolean>(true);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  const navigate = useNavigate();

  useEffect(() => {
    getSystems();
    getOs();
  }, []);

  useEffect(() => {
    if (options.length > 1) {
      getComplaints();
    }
  }, [options]);

  const initObj = () => {
    setDataObj({
      description: "",
      system_id: [],
      scroll_downs: [],
      priority: 0,
    });
  };

  const getSystems = async () => {
    setLoading(true);
    try {
      const res = await fetchData(systems);
      if (res.success) {
        const arr: SelectModel[] = [{ value: "null", label: "None" }];
        for (let index = 0; index < res.data.length; index++) {
          const x = res.data[index];
          arr.push({ value: x._id, label: x.description });
        }
        setOptions(arr);
      } else {
        setOptions([]);
        alert("No Systems found...!");
        navigate("/auth/sign-in");
      }
      setLoading(false);
    } catch (error: any) {
      const alreadyLoggedIn = error.message?.endsWith("401");
      if (alreadyLoggedIn) {
        setSnackbar({ open: true, message: "logged in from some other device", severity: "error" });
        setTimeout(() => {
          navigate("/auth/sign-in");
        }, 3000);
      }
    }
  };

  const applyFilterAndSort = (
    data: ComplaintsType[],
    sType: "description" | "system" | null,
    sVal: string | null
  ) => {
    let filtered = [...data];

    if (sType === "system" && sVal) {
      filtered = filtered.filter((row) => row.system_id?.includes(sVal));
      filtered.sort(
        (a, b) => getSystemPriority(a.priority, sVal) - getSystemPriority(b.priority, sVal)
      );
    } else if (sType === "description" && sVal) {
      if (sVal !== "__ALL__") {
        filtered = filtered.filter(
          (row) => row.description?.toLowerCase() === sVal.toLowerCase()
        );
      }
      filtered.sort((a, b) => getBasePriority(a.priority) - getBasePriority(b.priority));
    } else {
      // Default: sort by base priority
      filtered.sort((a, b) => getBasePriority(a.priority) - getBasePriority(b.priority));
    }

    const updatedRows = filtered.map((row: any) => {
      const text = options
        .filter((opt: any) => row.system_id?.includes(opt.value))
        .map((opt: any) => opt.label)
        .join(", ");

      return {
        ...row,
        systemNames: text,
      };
    });

    setRows(updatedRows);
  };

  const getComplaints = async () => {
    setLoading(true);
    try {
      const res = await fetchData(symptoms);
      if (res.success) {
        setAllComplaints(res.data);
        applyFilterAndSort(res.data, searchType, searchValue);
      } else {
        setAllComplaints([]);
        setRows([]);
        alert("No Complaint found...!");
      }
      setLoading(false);
    } catch (error: any) {
      const alreadyLoggedIn = error.message?.endsWith("401");
      if (alreadyLoggedIn) {
        setSnackbar({ open: true, message: "logged in from some other device", severity: "error" });
        setTimeout(() => {
          navigate("/auth/sign-in");
        }, 3000);
      }
    }
  };

  const getOs = async () => {
    try {
      setLoading(true);
      const res = await fetchData("OtherSymptoms");
      if (res.success) {
        let sortData = res.data.sort(
          (a: any, b: any) => getBasePriority(a.priority) - getBasePriority(b.priority)
        );
        setOsData(sortData);
      } else {
        setOsData([]);
      }
      setLoading(false);
    } catch (error: any) {
      const alreadyLoggedIn = error.message?.endsWith("401");
      if (alreadyLoggedIn) {
        setSnackbar({ open: true, message: "logged in from some other device", severity: "error" });
        setTimeout(() => {
          navigate("/auth/sign-in");
        }, 3000);
      }
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDataObj((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));

    setDataObjUpdate((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  // Handle Search Modal Apply
  const handleApplySearch = (
    type: "description" | "system",
    val: string,
    label: string
  ) => {
    setSearchType(type);
    setSearchValue(val);
    setSearchLabel(label);
    applyFilterAndSort(allComplaints, type, val);
  };

  // Handle Clear Search
  const handleClearSearch = () => {
    setSearchType(null);
    setSearchValue(null);
    setSearchLabel(null);
    applyFilterAndSort(allComplaints, null, null);
  };

  // Move Up
  const handleMoveUp = async (currentRow: ComplaintsType) => {
    if (loading) return;
    setLoading(true);
    try {
      if (searchType === "system" && searchValue) {
        const currentIndex = rows.findIndex((r) => r._id === currentRow._id);
        if (currentIndex <= 0) {
          setLoading(false);
          return;
        }
        const rowAbove = rows[currentIndex - 1];
        const pCurrent = getSystemPriority(currentRow.priority, searchValue);
        const pAbove = getSystemPriority(rowAbove.priority, searchValue);

        const currentPriorityObj =
          typeof currentRow.priority === "object" && currentRow.priority !== null
            ? { ...currentRow.priority }
            : { base: getBasePriority(currentRow.priority) };
        currentPriorityObj[searchValue] = pAbove;

        const abovePriorityObj =
          typeof rowAbove.priority === "object" && rowAbove.priority !== null
            ? { ...rowAbove.priority }
            : { base: getBasePriority(rowAbove.priority) };
        abovePriorityObj[searchValue] = pCurrent === pAbove ? pAbove + 1 : pCurrent;

        await updatetDataById(symptoms, { priority: currentPriorityObj }, currentRow._id);
        await updatetDataById(symptoms, { priority: abovePriorityObj }, rowAbove._id);
      } else if (searchType === "description") {
        const currentIndex = rows.findIndex((r) => r._id === currentRow._id);
        if (currentIndex <= 0) {
          setLoading(false);
          return;
        }
        const rowAbove = rows[currentIndex - 1];
        const pCurrent = getBasePriority(currentRow.priority);
        const pAbove = getBasePriority(rowAbove.priority);

        const currentPriorityObj =
          typeof currentRow.priority === "object" && currentRow.priority !== null
            ? { ...currentRow.priority }
            : { base: pCurrent };
        currentPriorityObj.base = pAbove;

        const abovePriorityObj =
          typeof rowAbove.priority === "object" && rowAbove.priority !== null
            ? { ...rowAbove.priority }
            : { base: pAbove };
        abovePriorityObj.base = pCurrent === pAbove ? pAbove + 1 : pCurrent;

        await updatetDataById(symptoms, { priority: currentPriorityObj }, currentRow._id);
        await updatetDataById(symptoms, { priority: abovePriorityObj }, rowAbove._id);
      }
      await getComplaints();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  // Move Down
  const handleMoveDown = async (currentRow: ComplaintsType) => {
    if (loading) return;
    setLoading(true);
    try {
      if (searchType === "system" && searchValue) {
        const currentIndex = rows.findIndex((r) => r._id === currentRow._id);
        if (currentIndex < 0 || currentIndex >= rows.length - 1) {
          setLoading(false);
          return;
        }
        const rowBelow = rows[currentIndex + 1];
        const pCurrent = getSystemPriority(currentRow.priority, searchValue);
        const pBelow = getSystemPriority(rowBelow.priority, searchValue);

        const currentPriorityObj =
          typeof currentRow.priority === "object" && currentRow.priority !== null
            ? { ...currentRow.priority }
            : { base: getBasePriority(currentRow.priority) };
        currentPriorityObj[searchValue] = pBelow;

        const belowPriorityObj =
          typeof rowBelow.priority === "object" && rowBelow.priority !== null
            ? { ...rowBelow.priority }
            : { base: getBasePriority(rowBelow.priority) };
        belowPriorityObj[searchValue] = pCurrent === pBelow ? pBelow - 1 : pCurrent;

        await updatetDataById(symptoms, { priority: currentPriorityObj }, currentRow._id);
        await updatetDataById(symptoms, { priority: belowPriorityObj }, rowBelow._id);
      } else if (searchType === "description") {
        const currentIndex = rows.findIndex((r) => r._id === currentRow._id);
        if (currentIndex < 0 || currentIndex >= rows.length - 1) {
          setLoading(false);
          return;
        }
        const rowBelow = rows[currentIndex + 1];
        const pCurrent = getBasePriority(currentRow.priority);
        const pBelow = getBasePriority(rowBelow.priority);

        const currentPriorityObj =
          typeof currentRow.priority === "object" && currentRow.priority !== null
            ? { ...currentRow.priority }
            : { base: pCurrent };
        currentPriorityObj.base = pBelow;

        const belowPriorityObj =
          typeof rowBelow.priority === "object" && rowBelow.priority !== null
            ? { ...rowBelow.priority }
            : { base: pBelow };
        belowPriorityObj.base = pCurrent === pBelow ? pBelow - 1 : pCurrent;

        await updatetDataById(symptoms, { priority: currentPriorityObj }, currentRow._id);
        await updatetDataById(symptoms, { priority: belowPriorityObj }, rowBelow._id);
      }
      await getComplaints();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const columns: GridColDef[] = [
    { field: "description", headerName: "Description", flex: 3 },
    {
      field: "systemNames",
      headerName: "Systems",
      flex: 3,
      renderCell: (params: any) => <>{params.row.systemNames}</>,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 2,
      sortable: false,
      renderCell: (params: any) => {
        let isUpDisabled = true;
        let isDownDisabled = true;

        if (isSearchActive) {
          const idx = rows.findIndex((r) => r._id === params.row._id);
          isUpDisabled = idx <= 0 || rows.length <= 1;
          isDownDisabled = idx < 0 || idx >= rows.length - 1 || rows.length <= 1;
        }

        return (
          <>
            {isSearchActive && (
              <>
                <Button
                  title="up"
                  variant="contained"
                  disabled={loading || isUpDisabled}
                  sx={{
                    width: "30px",
                    height: "30px",
                    minWidth: "30px",
                    mr: "5px",
                    padding: 0,
                    background: "#e2e8f0",
                    color: "#1b2559",
                    "&:hover": { background: "#e2e8f0", opacity: 0.8 },
                    "&:active": { background: "#e2e8f0", opacity: 0.7 },
                    "&:disabled": { background: "#e2e8f0", opacity: 0.5, cursor: "not-allowed" },
                  }}
                  onClick={() => handleMoveUp(params.row)}
                >
                  <BsArrowUp />
                </Button>
                <Button
                  title="down"
                  variant="contained"
                  disabled={loading || isDownDisabled}
                  sx={{
                    width: "30px",
                    height: "30px",
                    minWidth: "30px",
                    mr: "5px",
                    padding: 0,
                    background: "#e2e8f0",
                    color: "#1b2559",
                    "&:hover": { background: "#e2e8f0", opacity: 0.8 },
                    "&:active": { background: "#e2e8f0", opacity: 0.7 },
                    "&:disabled": { background: "#e2e8f0", opacity: 0.5, cursor: "not-allowed" },
                  }}
                  onClick={() => handleMoveDown(params.row)}
                >
                  <BsArrowDown />
                </Button>
              </>
            )}
            <Button
              title="Edit"
              variant="contained"
              sx={{
                width: "30px",
                height: "30px",
                minWidth: "30px",
                mr: "5px",
                padding: 0,
                background: "#eed202",
                color: "white",
                "&:hover": { background: "#eed202", opacity: 0.8 },
                "&:active": { background: "#eed202", opacity: 0.7 },
              }}
              onClick={() => {
                setDataObj((prev) => ({
                  ...prev,
                  description: params.row.description,
                  system_id: params.row.system_id,
                  scroll_downs: params.row.scroll_downs,
                }));
                setOpen(true);
                setIsEdit(true);
                setEditId(params.row._id);
              }}
            >
              <EditIcon fontSize="small" />
            </Button>
            <Button
              title="Delete"
              sx={{
                width: "30px",
                height: "30px",
                minWidth: "30px",
                mr: "5px",
                padding: 0,
                background: "#ff0000",
                color: "white",
                "&:hover": { background: "#ff0000", opacity: 0.8 },
                "&:active": { background: "#ff0000", opacity: 0.7 },
              }}
              variant="contained"
              onClick={async () => {
                /* eslint-disable no-restricted-globals */
                if (confirm(`Are you sure to delete ${params.row.description}`)) {
                  const res = await deleteDataById(symptoms, params.row._id);
                  if (res.success) {
                    getComplaints();
                  } else {
                    alert(res.message);
                  }
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </Button>
          </>
        );
      },
    },
  ];

  const handleRemoveCondition = (targetId: string, index: number) => {
    const data = { ...dataObj };
    const updatedScrollDowns = [
      ...data.scroll_downs.slice(0, index + 1),
      ...data.scroll_downs.slice(index + 1).map((item) => ({
        ...item,
        priority: item.priority - 1,
      })),
    ];
    data.scroll_downs = updatedScrollDowns.filter((obj) => obj.description !== targetId);
    setDataObj(data);
  };

  const handleAddCondition = () => {
    const data = dataObj;
    data.scroll_downs.push({
      description: "",
      priority: data.scroll_downs.length,
    });
    setDataObj({ ...data });
  };

  const handleConditionChange = (value: string, index: number) => {
    const data = dataObj;
    data.scroll_downs[index].description = value;
    setDataObj({ ...data });
  };

  const handleSelectAllSystems = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allSystemIds = options.slice(1).map((opt) => opt.value);
      setDataObj((prev) => ({ ...prev, system_id: allSystemIds }));
    } else {
      setDataObj((prev) => ({ ...prev, system_id: [] }));
    }
  };

  const handleSelectOSChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setIsOs(isChecked);
  };

  const handleObjectMoveUp = (arr: ScrollDown, currentObjIndex: number) => {
    if (currentObjIndex <= 0) return;
    const data = { ...dataObj };
    const beforeObjIndex = currentObjIndex - 1;

    let currentObject = data.scroll_downs[currentObjIndex];
    let beforeObject = data.scroll_downs[beforeObjIndex];
    if (!currentObject || !beforeObject) return;

    beforeObject.priority = beforeObject.priority + 1;
    currentObject.priority = currentObject.priority - 1;

    data.scroll_downs[currentObjIndex] = currentObject;
    data.scroll_downs[beforeObjIndex] = beforeObject;

    setDataObj(data);
  };

  const handleObjectMoveDown = (arr: ScrollDown, currentObjIndex: number) => {
    if (!dataObj?.scroll_downs || currentObjIndex >= dataObj.scroll_downs.length - 1) return;
    const data = { ...dataObj };
    const nextObjIndex = currentObjIndex + 1;

    let currentObject = data.scroll_downs[currentObjIndex];
    let nextObject = data.scroll_downs[nextObjIndex];
    if (!currentObject || !nextObject) return;

    nextObject.priority = nextObject.priority - 1;
    currentObject.priority = currentObject.priority + 1;

    data.scroll_downs[currentObjIndex] = currentObject;
    data.scroll_downs[nextObjIndex] = nextObject;

    setDataObj(data);
  };

  const handleDialogClose = () => {
    initObj();
    setOpen(false);
    setIsOs(false);
  };

  useEffect(() => {
    setDataObjUpdate({
      description: dataObj.description,
      system_id: dataObj.system_id,
      scroll_downs: dataObj.scroll_downs,
    });
  }, [dataObj]);

  // Options for SearchFilterModal
  const descriptionOptions: SearchOption[] = [
    { value: "__ALL__", label: "All Descriptions" },
    ...Array.from(new Set(allComplaints.map((c) => c.description)))
      .filter(Boolean)
      .map((desc) => ({ value: desc, label: desc })),
  ];

  const systemOptions: SearchOption[] = options
    .filter((o) => o.value !== "null")
    .map((o) => ({ value: o.value, label: o.label }));

  return (
    <div>
      <div className="mt-5">
        <Card extra={"w-full h-full sm:overflow-auto px-8"}>
          <header className="relative flex items-center justify-between py-5 flex-wrap gap-3">
            <h1 className="text-[26px] text-textPrimary font-semibold">Complaints</h1>

            <div className="flex items-center gap-3 flex-wrap">
              {isSearchActive && (
                <div className="flex items-center gap-2 bg-brand-50 border border-brand-200 dark:bg-navy-700 px-3 py-2 rounded-lg text-xs font-medium text-brand-600 dark:text-brand-300">
                  <span>
                    Filter: <b>{searchType === "system" ? "System" : "Description"}</b> (
                    {searchLabel || searchValue})
                  </span>
                  <button
                    onClick={handleClearSearch}
                    className="ml-1 text-red-500 hover:text-red-700 font-bold text-sm cursor-pointer"
                    title="Clear filter"
                  >
                    &times;
                  </button>
                </div>
              )}

              <button
                onClick={() => setSearchModalOpen(true)}
                className="flex items-center text-sm hover:cursor-pointer bg-surfaceSecondary p-3 text-brand-500 hover:bg-surfaceSecondary/80 active:bg-surfaceSecondary/70 linear justify-center rounded-lg font-bold transition duration-200"
              >
                <BsSearch className="mr-2" /> Search
              </button>

              <button
                onClick={() => {
                  setOpen(true);
                  setIsEdit(false);
                  initObj();
                }}
                className="flex items-center text-sm hover:cursor-pointer bg-surfaceSecondary p-3 text-brand-500 hover:bg-surfaceSecondary/80 active:bg-surfaceSecondary/70 linear justify-center rounded-lg font-bold transition duration-200"
              >
                Add New Complaint &nbsp;&nbsp; <BsFillNodePlusFill />
              </button>
            </div>
          </header>

          <GeneralModal
            openModal={open}
            title="Add Complaints"
            saveButtonText="Submit"
            cancelButtonText="Close"
            onClose={handleDialogClose}
            onSave={async () => {
              if (!dataObj.description || dataObj.description.trim() === "") {
                alert("Description required...!");
                return;
              }
              if (!isEdit) {
                const basePriority = allComplaints.length;
                let postdata: any = {
                  ...dataObj,
                  priority: createInitialPriority(basePriority, dataObj.system_id),
                };

                const res = await insertData(symptoms, postdata);
                if (res.success) {
                  if (isOs) {
                    let osPostData: any = {
                      ...dataObj,
                      priority: createInitialPriority(osData.length, dataObj.system_id),
                    };
                    await insertData("OtherSymptoms", osPostData);
                  }

                  getComplaints();
                  setIsOs(false);
                  setOpen(false);
                } else {
                  alert(res.message);
                }
              } else {
                const existing = allComplaints.find((c) => c._id === editId);
                const updatedPriority = ensureSystemPriorities(
                  existing?.priority,
                  dataObjUpdate.system_id
                );
                let postData: any = {
                  ...dataObjUpdate,
                  priority: updatedPriority,
                };
                const res = await updatetDataById(symptoms, postData, editId);
                if (res.success) {
                  getComplaints();
                  setOpen(false);
                } else {
                  alert(res.message);
                }
              }
              initObj();
            }}
          >
            <div className="mt-10 w-[100%]">
              <div className="w-full mr-5">
                <InputField
                  name="description"
                  variant="auth"
                  extra="mb-3"
                  label="Description"
                  placeholder="Description"
                  type="text"
                  id="description"
                  value={dataObj.description.replace("  ", " ")}
                  onChange={handleChange}
                />
              </div>

              <div className="w-full mt-[-8px]">
                <label
                  htmlFor="selectionType"
                  className="font-poppins text-sm text-textPrimary ml-1.5 font-medium"
                >
                  System
                </label>
                <ThemedSelect
                  name="filters"
                  placeholder="Filters"
                  value={options.filter((option) => dataObj.system_id.includes(option.value))}
                  options={options}
                  onChange={(selectedOptions: any) => {
                    const selectedValues = selectedOptions.map((option: any) => option.value);
                    const filteredValues = selectedValues.filter(
                      (value: string) => value !== "null"
                    );
                    setDataObj((prev) => ({
                      ...prev,
                      system_id: filteredValues,
                    }));
                  }}
                  isMulti={true}
                />
              </div>
              <div className="flex items-center gap-7">
                <div className="mt-3 flex items-center gap-2">
                  <h1 className="text-sm text-textPrimary">Add to all systems</h1>
                  <input
                    type="checkbox"
                    checked={dataObj.system_id.length === options.length - 1}
                    onChange={handleSelectAllSystems}
                  />
                </div>
                {isEdit ? null : (
                  <div className="mt-3 flex items-center gap-2">
                    <h1 className="text-sm text-textPrimary">
                      Create same record in other symptoms
                    </h1>
                    <input
                      type="checkbox"
                      checked={isOs}
                      onChange={handleSelectOSChange}
                    />
                  </div>
                )}
              </div>
            </div>
            {dataObj &&
              dataObj.scroll_downs &&
              dataObj.scroll_downs.length > 0 &&
              dataObj.scroll_downs
                .sort((a, b) => a.priority - b.priority)
                .map((x, index) => {
                  return (
                    <div key={index} className="flex items-end w-[100%]">
                      <div className="w-[450px] mr-5">
                        <InputField
                          variant="auth"
                          extra=""
                          label="Condition"
                          placeholder="Enter Condition"
                          id={x.description}
                          type="text"
                          value={x.description}
                          onChange={(e) => {
                            handleConditionChange(e.target.value, index);
                          }}
                        />
                      </div>
                      <button
                        disabled={index === 0 || x.priority === 0}
                        type="button"
                        className="flex justify-center items-center w-[50px] h-[50px] rounded-xl bg-[#e2e8f0] disabled:bg-gray-300 disabled:hover:cursor-not-allowed disabled:text-gray-400 py-[12px] text-base font-medium text-[#1b2559] transition duration-200 hover:bg-[#e2e8f0]/80 active:bg-[#e2e8f0]/70 dark:bg-[#e2e8f0] dark:text-[#1b2559] dark:hover:bg-[#e2e8f0]/80 dark:active:bg-[#e2e8f0]/70 ml-1"
                        onClick={() => handleObjectMoveUp(x, index)}
                      >
                        <BsArrowUp />
                      </button>
                      <button
                        type="button"
                        disabled={
                          index === dataObj.scroll_downs.length - 1 ||
                          x.priority === dataObj.scroll_downs.length - 1
                        }
                        className="flex justify-center items-center w-[50px] h-[50px] rounded-xl bg-[#e2e8f0] disabled:bg-gray-300 disabled:hover:cursor-not-allowed disabled:text-gray-400 py-[12px] text-base font-medium text-[#1b2559] transition duration-200 hover:bg-[#e2e8f0]/80 active:bg-[#e2e8f0]/70 dark:bg-[#e2e8f0] dark:text-[#1b2559] dark:hover:bg-[#e2e8f0]/80 dark:active:bg-[#e2e8f0]/70 ml-1"
                        onClick={() => handleObjectMoveDown(x, index)}
                      >
                        <BsArrowDown />
                      </button>
                      <button
                        onClick={() => handleRemoveCondition(x.description, index)}
                        type="button"
                        className="linear w-[50px] h-[50px] rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-500/80 active:bg-brand-500/70 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-400/80 dark:active:bg-brand-400/70 ml-1"
                      >
                        x
                      </button>
                    </div>
                  );
                })}
            <p
              onClick={() => {
                handleAddCondition();
              }}
              className="font-poppins text-sm text-textPrimary ml-1.5 font-normal mt-4 cursor-pointer"
            >
              <b>+</b> &nbsp; Add condition
            </p>
          </GeneralModal>

          <div>
            <DataTable
              columns={columns}
              rows={rows}
              loading={loading}
              hideSearch={true}
              onFilterActiveChange={setIsFiltered}
            />
          </div>
        </Card>
      </div>

      <SearchFilterModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        onApply={handleApplySearch}
        onClear={handleClearSearch}
        descriptionOptions={descriptionOptions}
        systemOptions={systemOptions}
        currentSearchType={searchType}
        currentSearchValue={searchValue}
      />

      <AppSnackbar
        isOpen={snackbar.open}
        title={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
      />
    </div>
  );
};

export default Complaints;
